import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { FileText, Download, Plus, CheckCircle2, XCircle, Clock, Send, Copy, Globe } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useBranchStore } from '@/stores';
import { VATLiabilityWidget } from '@/components/accounting/VATLiabilityWidget';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const STATUS_STYLES = {
  prepared:  { icon: Clock,        class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',    label: 'Prepared' },
  submitted: { icon: Send,         class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',        label: 'Submitted' },
  accepted:  { icon: CheckCircle2, class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'Accepted' },
  rejected:  { icon: XCircle,      class: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',        label: 'Rejected' },
};

const AUTHORITY_DESC = {
  sa: 'ZATCA · Saudi Arabia · 16-box VAT + Phase 2 e-invoicing',
  ae: 'FTA · United Arab Emirates · VAT 201',
  bh: 'NBR · Bahrain · 9-box VAT return',
  om: 'OTA · Oman · VAT return',
  gb: 'HMRC · UK · Making Tax Digital 9-box',
  in: 'GSTN · India · GSTR-3B 12-section',
  eg: 'ETA · Egypt · VAT return',
};

export default function TaxSubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [prepareForm, setPrepareForm] = useState({
    country: 'sa',
    branch_id: '',
    start_date: toLocalYmd(new Date(new Date().setDate(1))),
    end_date: localToday(),
    period_key: '',
  });
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/tax-submissions');
      setSubmissions(res.data.items || []);
    } catch { toast.error('Failed to load submissions'); }
  };

  useEffect(() => {
    fetchBranches();
    api.get('/accounting/tax-config/countries').then(r => setCountries(r.data)).catch(() => {});
    fetchData();
  }, []);

  const prepare = async () => {
    try {
      const body = { ...prepareForm };
      if (!body.branch_id) delete body.branch_id;
      if (!body.period_key) delete body.period_key;
      await api.post('/tax-submissions/prepare', body);
      toast.success('Payload prepared successfully');
      setPrepareOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Prepare failed'); }
  };

  const updateStatus = async (id, status, note = '', reference = '') => {
    try {
      await api.put(`/tax-submissions/${id}/status`, { status, note, reference });
      toast.success(`Marked as ${status}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const downloadPayload = async (id, authority, country) => {
    try {
      const res = await api.get(`/tax-submissions/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${authority || 'vat'}_${country}_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const copyPayload = async (payload) => {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Copied to clipboard');
  };

  const flag = (code) => {
    if (!code) return '🌍';
    return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="tax-submissions-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Send size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Tax Authority Submissions</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Prepare ZATCA / FTA / NBR / HMRC / GSTR-3B / Generic payloads — upload to authority portals
              </p>
            </div>
          </div>
          <Button onClick={() => setPrepareOpen(true)} data-testid="prepare-submission-btn"
            className="bg-sky-500 hover:bg-sky-600 text-white">
            <Plus size={14} className="mr-1" /> Prepare Submission
          </Button>
        </div>

        {/* iter434 — VAT Liability mini-dashboard. Sits above the
            submissions table so the user can see net VAT owed at a glance
            and one-click jump to record the settlement. */}
        <VATLiabilityWidget />

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><FileText size={14}/> Recent Submissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Country</th>
                    <th className="text-left px-4 py-2 font-semibold">Authority</th>
                    <th className="text-left px-4 py-2 font-semibold">Period</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Prepared By</th>
                    <th className="text-left px-4 py-2 font-semibold">Prepared At</th>
                    <th className="text-right px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                      No submissions yet. Click "Prepare Submission" to generate a payload for a period.
                    </td></tr>
                  ) : submissions.map(s => {
                    const statusStyle = STATUS_STYLES[s.status] || STATUS_STYLES.prepared;
                    const StatusIcon = statusStyle.icon;
                    return (
                      <tr key={s.id} className="border-b border-border hover:bg-muted/30" data-testid={`submission-${s.id}`}>
                        <td className="px-4 py-2 font-medium">
                          <span className="mr-1.5">{flag(s.country)}</span>{s.country?.toUpperCase()}
                        </td>
                        <td className="px-4 py-2">
                          <span className="font-semibold">{s.authority}</span>
                          <p className="text-[10px] text-muted-foreground">{(AUTHORITY_DESC[s.country] || '').split('·').slice(2).join('·')}</p>
                        </td>
                        <td className="px-4 py-2 text-xs font-mono">
                          {s.period_start} → {s.period_end}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusStyle.class}`}>
                            <StatusIcon size={11} /> {statusStyle.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{s.prepared_by}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground font-mono">{(s.prepared_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => { setSelected(s); setViewOpen(true); }}
                              className="px-2 py-1 rounded hover:bg-muted text-xs" title="View payload">
                              <FileText size={12} />
                            </button>
                            <button onClick={() => downloadPayload(s.id, s.authority, s.country)}
                              className="px-2 py-1 rounded hover:bg-muted text-xs" title="Download JSON"
                              data-testid={`download-${s.id}`}>
                              <Download size={12} />
                            </button>
                            <button onClick={() => copyPayload(s.payload)}
                              className="px-2 py-1 rounded hover:bg-muted text-xs" title="Copy">
                              <Copy size={12} />
                            </button>
                            {s.status === 'prepared' && (
                              <button onClick={() => updateStatus(s.id, 'submitted')}
                                className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-medium">
                                Mark Submitted
                              </button>
                            )}
                            {s.status === 'submitted' && (
                              <>
                                <button onClick={() => updateStatus(s.id, 'accepted')}
                                  className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-xs font-medium">
                                  Accepted
                                </button>
                                <button onClick={() => updateStatus(s.id, 'rejected')}
                                  className="px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 text-xs font-medium">
                                  Rejected
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-muted/30">
          <CardContent className="pt-4 flex items-start gap-3">
            <Globe size={18} className="shrink-0 text-info mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-semibold text-foreground">About tax authority submissions</p>
              <p className="text-muted-foreground">
                This module generates the exact JSON/XML payload each tax authority consumes, based on your VAT return data for the period. <strong>Download the payload and upload it through the authority's portal.</strong> Real-time API submission (ZATCA Phase 2 signing, HMRC MTD OAuth, FTA API) requires per-tenant sandbox credentials which must be configured separately by your integrator.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Prepare dialog */}
        <Dialog open={prepareOpen} onOpenChange={setPrepareOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Prepare Tax Authority Payload</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Country / Authority</Label>
                <Select value={prepareForm.country} onValueChange={(v) => setPrepareForm({ ...prepareForm, country: v })}>
                  <SelectTrigger data-testid="prep-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.entries(AUTHORITY_DESC).map(([code, desc]) => (
                      <SelectItem key={code} value={code}>{flag(code)} {desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Branch (optional)</Label>
                <Select value={prepareForm.branch_id || 'all'} onValueChange={(v) => setPrepareForm({ ...prepareForm, branch_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="prep-branch-select"><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches (tenant-level)</SelectItem>
                    {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Period Start</Label>
                  <Input type="date" value={prepareForm.start_date}
                    onChange={(e) => setPrepareForm({ ...prepareForm, start_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Period End</Label>
                  <Input type="date" value={prepareForm.end_date}
                    onChange={(e) => setPrepareForm({ ...prepareForm, end_date: e.target.value })} />
                </div>
              </div>
              {prepareForm.country === 'gb' && (
                <div>
                  <Label className="text-xs">HMRC Period Key <span className="text-muted-foreground">(e.g. 24A1)</span></Label>
                  <Input value={prepareForm.period_key}
                    onChange={(e) => setPrepareForm({ ...prepareForm, period_key: e.target.value })} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrepareOpen(false)}>Cancel</Button>
              <Button onClick={prepare} data-testid="save-prep-btn"
                className="bg-sky-500 hover:bg-sky-600 text-white">Generate Payload</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View payload dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-3xl max-h-[80dvh] overflow-y-auto">
            <DialogHeader><DialogTitle>Submission Payload</DialogTitle></DialogHeader>
            {selected && (
              <pre className="bg-muted p-3 rounded text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selected.payload || {}, null, 2)}
              </pre>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
              {selected && (
                <Button onClick={() => copyPayload(selected.payload)}>
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
